import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  animation = 'pulse',
}) => {
  const baseClasses = 'bg-slate-200';

  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer',
    none: '',
  };

  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: '',
    rounded: 'rounded-lg',
  };

  const style: React.CSSProperties = {
    width: width ?? (variant === 'text' ? '100%' : undefined),
    height: height ?? (variant === 'text' ? '1em' : undefined),
  };

  return (
    <div
      className={`${baseClasses} ${animationClasses[animation]} ${variantClasses[variant]} ${className}`}
      style={style}
    />
  );
};

// Pre-built skeleton layouts for common components

export const MundusCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-full">
    <div className="flex items-center gap-4 mb-4">
      <Skeleton variant="rounded" width={48} height={48} />
      <div className="flex-1">
        <Skeleton variant="text" width="60%" height={24} className="mb-2" />
        <Skeleton variant="text" width="40%" height={16} />
      </div>
    </div>
    <Skeleton variant="text" width="100%" height={14} className="mb-2" />
    <Skeleton variant="text" width="80%" height={14} className="mb-4" />
    <div className="flex gap-2 mt-4">
      <Skeleton variant="rounded" width={80} height={28} />
      <Skeleton variant="rounded" width={80} height={28} />
    </div>
  </div>
);

export const MundusGridSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <MundusCardSkeleton key={i} />
    ))}
  </div>
);

export const ChatMessageSkeleton: React.FC<{ isUser?: boolean }> = ({ isUser = false }) => (
  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
    <div className={`max-w-[80%] rounded-lg p-3 ${isUser ? 'bg-slate-100' : 'bg-white border border-slate-200'}`}>
      {!isUser && <Skeleton variant="text" width={100} height={14} className="mb-2" />}
      <Skeleton variant="text" width="100%" height={14} className="mb-1" />
      <Skeleton variant="text" width="70%" height={14} className="mb-2" />
      <Skeleton variant="text" width={60} height={12} />
    </div>
  </div>
);

export const ChatSkeleton: React.FC = () => (
  <div className="space-y-4 p-4">
    <ChatMessageSkeleton isUser={false} />
    <ChatMessageSkeleton isUser={true} />
    <ChatMessageSkeleton isUser={false} />
  </div>
);

export const DailyPlanSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex-1">
        <Skeleton variant="text" width="50%" height={28} className="mb-2" />
        <Skeleton variant="text" width="30%" height={16} />
      </div>
      <Skeleton variant="rounded" width={100} height={32} />
    </div>
    <div className="space-y-3 mt-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
          <Skeleton variant="rounded" width={20} height={20} />
          <Skeleton variant="text" width="80%" height={16} />
        </div>
      ))}
    </div>
  </div>
);

export const ProfileSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-4">
      <Skeleton variant="circular" width={80} height={80} />
      <div className="flex-1">
        <Skeleton variant="text" width="40%" height={28} className="mb-2" />
        <Skeleton variant="text" width="30%" height={16} />
      </div>
    </div>
    <div className="space-y-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i}>
          <Skeleton variant="text" width={120} height={14} className="mb-2" />
          <Skeleton variant="rounded" width="100%" height={40} />
        </div>
      ))}
    </div>
  </div>
);

export const TableRowSkeleton: React.FC<{ columns?: number }> = ({ columns = 4 }) => (
  <tr>
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Skeleton variant="text" width="80%" height={16} />
      </td>
    ))}
  </tr>
);

export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4
}) => (
  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
    <table className="w-full">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          {Array.from({ length: columns }).map((_, i) => (
            <th key={i} className="px-4 py-3 text-left">
              <Skeleton variant="text" width={80} height={14} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <TableRowSkeleton key={i} columns={columns} />
        ))}
      </tbody>
    </table>
  </div>
);

export default Skeleton;
